import {
    redundantAnnotations,
    redundantMatchingPatterns,
    stretchAminoAcids,
    identityColor,
    histogramPath,
    alignmentSizes,
    getMatchingPatterns,
    rulerPositions
} from "./utils";

import {
    alignBelowAnnotations,
    mergeAnnotations,
    isInRange,
    textSize,
    isInsideArea,
    attributeAnnotations,
    alignBpPosition,
    sortBy,
    truncate, linearDomain,
} from "../editor/utils";

import {
    endCodingSequencePath
} from "../mixins/linearShape";

import {
    colors,
    direction,
    Selector,
    thresholdLevels,
    zoomLevels,
} from '../mixins/dictionaries';

import {
    bottomPadding,
    paddingLinearEditor,
} from "../mixins/constants"

import Brush from "./linearBrush";
import Read from "./read";

import {select, selectAll} from "d3-selection";
import {scaleLinear} from "d3-scale";
import {axisBottom} from "d3-axis";
import {line} from "d3-shape";

import {viewMixins, brushMixins, codingSequenceMixins, annotationEventsMixins} from "./alignmentMixins";
import {annotationMixins} from "../mixins/linearMixins";

const d3 = {select, selectAll, scaleLinear, axisBottom, line}

export default class Alignment {
    constructor(settings, mediator) {
        this.settings = {
            ...settings
        }
        this.slots = [];
        this.mediator = mediator;

        Object.assign(this, viewMixins, brushMixins, annotationMixins, codingSequenceMixins, annotationEventsMixins);

        this.EventsOn();

        this.initContainer();
        this.brush = new Brush();
    }

    initData(data) {
        this.settings.id = data.friendly_id;

        this.positions = data.result.positions;

        this.consensus = data.result.consensus;
        this.coverage = data.result.coverage;
        this.identities = data.result.identity || [];

        this.reads = data.result.reads;

        this.referenceLength = data.result.reference.length;
        this.sequenceLength = this.referenceLength;
        this.fontSizeBase = (this.regularSizeFont.width / zoomLevels.mid);

        this.maxCoverageValue = Math.max(...this.coverage);
        this.maxChromatogramValue = 0;
        this.maxQualityValue = 0;
        const maxHeightHistogram = 22;
        const minHeightStepHistogram = 5;

        this.annotationHeight = 20;
        this.stepLevelAnnotation = 5;

        this.coverageHeightStep = Math.min(minHeightStepHistogram, maxHeightHistogram / Math.max(...this.coverage));

        if (this.identities?.length) {
            this.maxIdentityValue = Math.max(...this.identities);
            this.identityHeightStep = Math.min(minHeightStepHistogram, maxHeightHistogram / Math.max(...this.identities));
        }

        this.tempStartOffsetAlignment = 0;
        this.tempEndOffestAlignment = 0;

        [this.sequenceLength, this.tempStartOffsetAlignment, this.tempEndOffestAlignment, this.maxChromatogramValue, this.maxQualityValue] = alignmentSizes(this.referenceLength, this.reads);
        this.referenceMatchingPatterns = getMatchingPatterns(0, 0, this.referenceLength, 0, data.result.mismatches, data.result.deletions, data.result.insertions);

        this.sequence = data.result.reference;

        this.protocolAnnotations = {
            context: {
                pk: null,
                type: null,
            },
            annotations: []
        };

        [this.annotations, this.maxBelowLevels] = alignBelowAnnotations(mergeAnnotations(data.result, [], this.protocolAnnotations, this.referenceLength, []));

        this.maxBelowLevels += 1;

        this.matchingPatternInsertions = this.referenceMatchingPatterns.filter(m => {
            return m.type === "ins"
        });

        this.annotations.forEach(a => {
            if (a.hasOwnProperty("amino_sequence")) {
                a.origin_start = a.start;
                a.origin_end = a.end;
                a.origin_direction = a.direction;
            }
        });

        if (this.sequenceLength > this.referenceLength) {
            this.coverage = [...this.coverage.slice(this.referenceLength - this.tempStartOffsetAlignment, this.referenceLength), ...this.coverage, ...this.coverage.slice(0, (this.sequenceLength - this.referenceLength))]
            this.identities = [...this.identities.slice(this.referenceLength - this.tempStartOffsetAlignment, this.referenceLength), ...this.identities, ...this.identities.slice(0, (this.sequenceLength - this.referenceLength))]
            this.consensus = [...this.consensus.slice(this.referenceLength - this.tempStartOffsetAlignment, this.referenceLength), ...this.consensus, ...this.consensus.slice(0, (this.sequenceLength - this.referenceLength))]
            this.sequence = ([...this.sequence.slice(this.referenceLength - this.tempStartOffsetAlignment, this.referenceLength), ...this.sequence, ...this.sequence.slice(0, (this.sequenceLength - this.referenceLength))]).join("");

            this.annotations = [...this.annotations, ...redundantAnnotations(this.tempStartOffsetAlignment, this.tempEndOffestAlignment, this.annotations, this.referenceLength, this.sequenceLength)];
            this.positions = [
                ...data.result.positions.slice(this.referenceLength - this.tempStartOffsetAlignment, this.referenceLength),
                ...this.positions,
                ...data.result.positions.slice(0, this.tempEndOffestAlignment)
            ];
        } else {
            this.annotations.forEach(a => {
                if (a.start > a.end) {
                    const redundantAnnotation = {...a};
                    redundantAnnotation.start = 0;
                    redundantAnnotation.end = a.end;
                    redundantAnnotation.redundant = true;

                    this.annotations.push(redundantAnnotation);
                    a.end = this.sequenceLength
                }

            })
        }

        this.matchingPatternInsertionsSorted = [...this.matchingPatternInsertions, ...redundantMatchingPatterns(this.tempStartOffsetAlignment, this.tempEndOffestAlignment, this.matchingPatternInsertions, this.referenceLength, this.sequenceLength)].sort(sortBy("start"));

        stretchAminoAcids(this.annotations.filter(a => {
            return a.hasOwnProperty("amino_sequence")
        }), this.matchingPatternInsertionsSorted);

        this.slots[0] = {
            start: 0,
            end: this.sequenceLength
        };

        this.rangeBp = {
            start: 0,
            end: 0,
        };

        this.initPosition = 0;

        this.offsetScroll = {
            "current": this.initPosition,
            "step": 0,
        }

        this.searchResult = {
            data: [],
            textLength: 0
        };

        this.setEditorParameters();

        this.getEdges(this.maxBasePairsInAreaView, this.sequenceLength);

        this.EventsTrigger("setAreaView", {
            position: this.offsetScroll.current,
            width: this.maxBasePairsInAreaView
        })

        this.createBrush();

        this.annotationsContainer = this.mainBox.append('g')
            .attr("id", "annotationContainer")
            .attr("transform", "translate(0,0)");

        this.setComponentPositions(this.maxBelowLevels);

        this.hiddenAnnotations = [];
        this.initReads();
    }

    initContainer() {
        const linearPreviewHeight = 120;
        const referenceContainerHeight = 180;
        this.labelContainerWidth = 200;
        const bottomPadding = 25;

        this.$referenceContainer = document.querySelector(Selector.REFERENCE);
        this.$referenceContainer.style.height = referenceContainerHeight + "px";

        this.$mainBoxContainer = this.$referenceContainer.getBoundingClientRect();

        this.$alignmentLabelContainer = document.querySelector(Selector.REFERENCE_LABEL);
        this.$alignmentLabelContainer.style.height = referenceContainerHeight + "px";
        this.$alignmentLabelContainer.style.width = this.labelContainerWidth + "px";

        this.alignmentLabel = d3.select(Selector.REFERENCE_LABEL).append("svg")
        this.alignmentLabel.style("width", this.labelContainerWidth + "px");

        this.mainBox = d3.select(Selector.REFERENCE_SEQUENCE).append("svg")
            .style("height", referenceContainerHeight + "px")

        this.$readsBoxContainer = document.querySelector(Selector.READS);
        this.$readsBoxContainer.style.height = (window.innerHeight - this.$mainBoxContainer.top - linearPreviewHeight - referenceContainerHeight - bottomPadding) + "px";

        this.zoom = 12;
        this.fontHeight = 12;
        this.userAgent = window.navigator.userAgent;

        this.regularSizeFont = textSize("ACGTACGTACGT", Selector.REFERENCE_SEQUENCE);
        this.$containerWidth = (this.$mainBoxContainer.width - this.labelContainerWidth) - paddingLinearEditor;

        this.mainBox.style("width", this.$containerWidth + "px")

        this.addEventsMainContainer();
    }

    initReads() {
        const readHeight = 100;
        this.readsContainer = [];
        this.readSlots = [];

        const addLabelRead = (container, label, content) => {
            container
                .append("tspan")
                .attr("x", 15)
                .attr("dy", 15)
                .text(label);

            container
                .append("tspan")
                .text(content);
        }

        this.reads.forEach((r, index) => {
            const $divReadsContainer = document.createElement('div');
            $divReadsContainer.classList.add("col-lg-12");

            const $containerRowRead = document.createElement('div');
            $containerRowRead.classList.add("m-0", "d-flex", "align-items-center", "rounded", "justify-content-left")

            const $labelRead = document.createElement('div');
            $labelRead.classList.add("info", "bg-white", "border", "border-gray-200");
            $labelRead.style.height = readHeight + "px";
            $labelRead.style.width = this.labelContainerWidth + "px";

            const labelContainerRead = d3.select($labelRead).append("svg")
                .style("height", readHeight + "px")
                .style("width", this.labelContainerWidth + "px")
                .append("text")
                .attr("y", "10")

            addLabelRead(labelContainerRead, "ID: ", truncate(r.code, 20));
            addLabelRead(labelContainerRead, "Name: ", truncate(r.name, 13));
            addLabelRead(labelContainerRead, "Direction: ", (direction[r.direction] || "-"));
            addLabelRead(labelContainerRead, "Score: ", (Number(r.score).toFixed(2) || "-"));

            const $contentRead = document.createElement('div');
            $contentRead.classList.add("bg-white", "border", "border-gray-200");
            $contentRead.style.height = readHeight + "px";
            $contentRead.style.width = this.$containerWidth + "px";

            $containerRowRead.appendChild($labelRead)
            $containerRowRead.appendChild($contentRead)

            this.$readsBoxContainer.appendChild($containerRowRead)

            if (r.sequence) {
                const slots = [];

                slots.push({
                    start: this.tempStartOffsetAlignment + (r.start - r.start_trim),
                    end: this.tempStartOffsetAlignment + (r.end + r.end_trim),
                })

                if (isInsideArea((r.end + r.end_trim), (r.end + r.end_trim), this.referenceLength - this.tempStartOffsetAlignment, this.sequenceLength)) {
                    slots.push({
                        start: this.tempStartOffsetAlignment + (r.start - r.start_trim) - this.referenceLength,
                        end: this.tempStartOffsetAlignment + (r.end + r.end_trim) - this.referenceLength,
                    })
                }

                this.readsContainer[index] = new Read($contentRead, slots, this.$mainBoxContainer, this.regularSizeFont, this.tempStartOffsetAlignment, this.referenceLength, this.sequenceLength, this.$containerWidth, this.mediator);
                this.readsContainer[index].setData(r);

                this.readSlots.push(slots);
            }
        })
    }

    overEdges(eventX, scroll) {
        let brushX1
        let brushX2
        let newBrushSize
        if (scroll < 0) {
            brushX1 = ((this.rangeBp.start - this.offsetScroll.current) + scroll) * this.fontBoxWidth
            brushX2 = eventX
            newBrushSize = {
                x: brushX1,
                width: brushX2 - brushX1,
            }
        } else {
            brushX1 = eventX
            brushX2 = ((this.rangeBp.end - this.offsetScroll.current) - scroll) * this.fontBoxWidth
            newBrushSize = {
                x: brushX1,
                width: brushX2 - brushX1,
            }
        }

        this.brush.setParameters(newBrushSize)
    }

    scroll(wheelEvent) {
        if (wheelEvent.sourceEvent && wheelEvent.type === "zoom") {
            const scroll = wheelEvent.sourceEvent.wheelDelta < 0 ? -1 : 1;
            this.setScroll(scroll);
            this.EventsTrigger("setScroll", scroll);

            this.slide(wheelEvent.type);

            this.EventsTrigger("slide", wheelEvent.type);
        } else {
            if (wheelEvent.type === "start") {

                this.brush.hide();
                this.EventsTrigger("hideBrush")

                this.EventsTrigger("slide", wheelEvent.type);
            } else {
                const newBrushSize = {
                    x: (this.rangeBp.start - this.offsetScroll.current) * this.fontBoxWidth,
                    width: (this.rangeBp.end - this.rangeBp.start) * this.fontBoxWidth,
                }
                this.brush.setParameters(newBrushSize)
                this.brush.show();

                this.EventsTrigger("showBrush")
                this.EventsTrigger("updateBrush", newBrushSize)

                this.offsetScroll.step = 0;
                this.middleArea = alignBpPosition(this.offsetScroll.current, Math.round(this.maxBasePairsInAreaView / 2));

                this.slide(wheelEvent.type);
                this.EventsTrigger("slide", wheelEvent.type);

                this.scrollToFirstRead();
            }
        }
    }

    setData(data) {
        this.EventsTrigger("setData", data)
        this.initData(data)
        this.render();
    }

    render() {
        this.renderLabels();

        this.renderCoverages()
        this.renderIdentities();

        this.drawSequence();
        this.drawAnnotations();

        this.scrollToFirstRead();

        this.drawBorders([this.tempStartOffsetAlignment, this.tempStartOffsetAlignment + this.referenceLength]);

        this.enableTooltip();
    }

    renderLabels() {
        const defs = this.alignmentLabel.append('svg:defs');

        defs.append("marker")
            .attr("id", "markerEdge")
            .attr('viewBox', '0 -10 20 30')
            .attr("refX", 0)
            .attr("refY", 0)
            .attr('markerWidth', 30)
            .attr('markerHeight', 30)
            .attr("orient", "auto")
            .append("path")
            .attr('d', 'M 0,0 L 0,5')
            .style("fill", "#000")
            .style('stroke', '#000');

        this.alignmentLabel
            .append("text")
            .attr("x", 15)
            .attr("dy", this.positionsY.coverageField - 7.5)
            .text("Coverage");

        const coverageDetailContainer = this.alignmentLabel.append("g");

        coverageDetailContainer
            .append("text")
            .attr("x", 190)
            .attr("dy", this.positionsY.identityField - 32)
            .style("text-anchor", "end")
            .attr("font-size", 10)
            .text(0)

        coverageDetailContainer
            .append("text")
            .attr("x", 190)
            .attr("dy", this.positionsY.identityField - 42)
            .style("text-anchor", "end")
            .attr("font-size", 10)
            .text(Math.max(...this.coverage))

        coverageDetailContainer
            .append("line")
            .attr("x1", 193)
            .attr("y1", this.positionsY.coverageField - 22)
            .attr("x2", 193)
            .attr("y2", this.positionsY.coverageField - 2)
            .attr("stroke", "#000")
            .attr("stroke-width", .5)
            .attr("marker-start", "url(#markerEdge)")
            .attr("marker-end", "url(#markerEdge)")


        if (this.identities?.length) {
            this.alignmentLabel
                .append("text")
                .attr("x", 15)
                .attr("dy", this.positionsY.identityField)
                .text("Identity");
        }

        this.alignmentLabel
            .append("text")
            .attr("x", 15)
            .attr("dy", this.positionsY.disagreementField)
            .text("Disagreement");

        this.alignmentLabel
            .append("text")
            .attr("x", 15)
            .attr("dy", this.positionsY.referenceField)
            .text("Reference");
    }

    renderRuler() {
        const domain = linearDomain([0, this.sequenceLength], this.$containerWidth);
        const tickHeight = 4;
        const tickRelativePositions = rulerPositions(this.positions);

        this.mainBox.append("line")
            .attr("class", "ruler")
            .attr("transform", "translate(0," + this.positionsY.disagreementField + ")")
            .attr("x1", 0)
            .attr("y1", 0)
            .attr("x2", domain(this.sequenceLength))
            .attr("y2", 0)
            .style("stroke", "#000")
            .style("stroke-width", ".2")
            .style("opacity", "1")

        tickRelativePositions.forEach(t => {
            this.drawTick(this.mainBox, this.positionsY.disagreementField, t, tickHeight, domain);
        })
    }

    drawTick(container, layer, tick, height, domain) {
        const positionTick = domain(tick.position)
        const tickContainer = container.append("g")
            .attr("class", "tickGroup")
            .attr("transform", "translate( " + positionTick + "," + layer + ")");

        tickContainer.append("line")
            .attr("x1", 0)
            .attr("y1", height)
            .attr("x2", 1)
            .attr("y2", height)
            .style("stroke-width", "5")
            .style("opacity", "1")

        const tickNumberContainer = tickContainer
            .append("text")
            .attr("class", "tick")
            .attr("x", 0)
            .attr("dy", 15)
            .style("text-anchor", "middle")
            .text(tick.text);

        const tickContainerSize = tickContainer.node().getBBox().width;

        if (positionTick < (tickContainerSize / 2) ||
            positionTick > this.$containerWidth - (tickContainerSize / 2)
        ) {
            tickNumberContainer.style("text-anchor", positionTick < (tickContainerSize / 2) ? "start" : "end")
        }
    }

    drawAnnotations() {
        if (this.reads.length !== 0 && this.maxBelowLevels) {
            this.annotationHeight = Math.min(20, ((this.$mainBoxContainer.height - this.positionsY.annotationsField - (STEP * this.maxBelowLevels)) / this.maxBelowLevels))
        }
        const arrowLength = .4;
        let layer = 0;

        this.annotationsContainer.selectAll(".annotationGroup").remove();

        this.annotations.forEach(a => {
            if (isInsideArea(a.start, a.end, this.edges.left, this.edges.right, this.sequenceLength)) {
                layer = this.positionsY.annotationsField + ((this.stepLevelAnnotation + this.annotationHeight) * a.level)

                if (a.hasOwnProperty("amino_sequence")) {
                    const codonContainers = this.drawStretchedCodingSequence(this.annotationsContainer, a, layer, this.annotationHeight);

                    if (!a.visible) {
                        codonContainers.forEach(codon => {
                            codon.attr("data-popper-reference-hidden", "");
                        })
                    }
                    this.addCodingSequenceEvents(codonContainers, a);

                    /**
                     * end of Coding Sequence
                     * */
                    if (this.threshold !== thresholdLevels.full) {
                        const positionEnd = a.direction === 1 ? a.end : a.start;
                        this.annotationsContainer
                            .append("path")
                            .attr("transform", "translate(" + (positionEnd * this.fontBoxWidth) + ", " + layer + ")")
                            .attr("d", endCodingSequencePath(0, this.annotationHeight, 0, a.direction, arrowLength, this.fontBoxWidth))
                            .attr("class", "annotationGroup annotation_" + a.pk)
                            .style("fill", "none")
                            .style("stroke", "#000")
                            .style("stroke-width", 2)
                            .style("opacity", .5)
                    }

                } else {
                    const attributeAnnotation = attributeAnnotations(a.role_id, this.settings.annotation_colors);
                    const label = a.name ? a.name : "Unknown " + attributeAnnotation.name;

                    const annotationContainer = this.annotationsContainer
                        .append("g")
                        .attr("transform", "translate(" + (a.start * this.fontBoxWidth) + ", " + layer + ")")
                        .attr("class", "annotationGroup annotation annotation_" + a.pk)
                        .attr('data-toggle', 'virtual-popper')
                        .attr("data-virtual-popper-content", label);

                    if (!a.visible) {
                        annotationContainer.attr("data-popper-reference-hidden", "");
                    }

                    this.drawPlainAnnotation(annotationContainer, a, attributeAnnotation.color, label, this.annotationHeight)
                    this.addAnnotationEvents(annotationContainer, a, a.start, a.end);
                }
            }
        })
    }

    renderCoverages() {
        const line = d3.line();
        const coverageHeightStep = 22 / this.maxCoverageValue;
        const coverageSlice = this.coverage.slice(this.edges.left, this.edges.right)
        const coverageNode = this.mainBox.selectAll(".coverage").nodes();
        const path = histogramPath(coverageSlice, coverageHeightStep, 0.05, this.fontBoxWidth);

        if (!coverageNode.length) {
            const coverageContainer = this.mainBox
                .append("path")
                .attr("class", "coverage")
                .attr("transform", "translate(0, " + this.positionsY.coverageField + ")")
                .attr("opacity", ".7")
                .attr("fill", "#c0c0c0")
                .attr("stroke", "#c0c0c0")
                .attr("d", line(path));
        } else {
            coverageNode[0]
                .setAttribute("d", line(path))

            if (this.threshold !== thresholdLevels.full) {
                coverageNode[0].style.strokeWidth = .2
            } else {
                coverageNode[0].style.strokeWidth = .1
            }
        }
    }

    renderIdentities() {
        const identityContainerYPosition = this.positionsY.identityField;
        const identitySlice = this.identities.slice(this.edges.left, this.edges.right)

        if (this.zoom === zoomLevels.max) {
            this.mainBox.selectAll(".identity,.identityFullSize").remove();
            if (this.identities.length) {
                this.mainBox
                    .append("line")
                    .attr("class", "identityFullSize")
                    .attr("transform", "translate(0, " + identityContainerYPosition + ")")
                    .attr("x1", 0)
                    .attr("y1", -10)
                    .attr("x2", this.sequenceLength)
                    .attr("y2", -10)
                    .attr("stroke", identityColor(100, this.maxIdentityValue))
                    .attr("stroke-width", 20)
                    .style("opacity", ".5")

                const discordantIdentities = this.identities.map((id, index) => [id, index]).filter(([value, index]) => value < 90);
                discordantIdentities.forEach(di => {
                    this.mainBox
                        .append("line")
                        .attr("class", "identityFullSize")
                        .attr("x1", (this.fontBoxWidth * di[1]))
                        .attr("y1", (identityContainerYPosition - this.identityHeightStep * di[0]))
                        .attr("x2", (this.fontBoxWidth * di[1]))
                        .attr("y2", (identityContainerYPosition))
                        .attr("fill", identityColor(di[0], this.maxIdentityValue))
                        .attr("stroke", identityColor(di[0], this.maxIdentityValue))
                        .attr("stroke-width", 1)
                        .style("opacity", ".5")
                })
            }

        } else {
            this.mainBox.selectAll(".identityFullSize").remove();
            const identitySliceNodes = this.mainBox.selectAll(".identity").nodes();

            identitySlice.forEach((id, index) => {
                const identityHeight = this.identityHeightStep * id
                let color = identityColor(id, this.maxIdentityValue);

                if (identitySliceNodes[index]) {
                    identitySliceNodes[index].setAttribute("x", index * this.fontBoxWidth)
                    identitySliceNodes[index].setAttribute("y", identityContainerYPosition - identityHeight)
                    identitySliceNodes[index].setAttribute("height", identityHeight)
                    identitySliceNodes[index].setAttribute("width", this.fontBoxWidth)
                    identitySliceNodes[index].style.fill = color
                    identitySliceNodes[index].dataset.virtualPopperContent = id;
                } else {
                    this.mainBox
                        .append("rect")
                        .attr("class", "identity")
                        .attr("x", index * this.fontBoxWidth)
                        .attr("y", identityContainerYPosition - identityHeight)
                        .attr("height", identityHeight)
                        .attr("width", this.fontBoxWidth)
                        .style("fill", color)
                        .style("stroke-width", .1)
                        .style("stroke", "#000")
                        .attr("opacity", ".5")
                        .attr('data-toggle', 'virtual-popper')
                        .attr("data-virtual-popper-content", id);
                }
            })

            if (identitySliceNodes.length >= identitySlice.length) {
                for (let i = identitySlice.length; i < identitySliceNodes.length; i++) {
                    identitySliceNodes[i].remove();
                }
            }
        }
    }

    sequenceSlice() {
        const sequence = this.getSequence(this.edges.left, this.edges.right)
        let translationX = this.fontBoxWidth / 2;

        return [sequence, translationX];
    }

    drawSequence() {
        let [sequence, translationX] = this.sequenceSlice();
        let consensusIndex = this.edges.left;

        this.mainBox.selectAll(".basePairSequence,.basePairDisagreement,.ruler,.tickGroup").remove();

        sequence.forEach(bp => {
            const tmpTranslationX = translationX;
            translationX += this.fontBoxWidth;

            let disagreement = this.consensus[consensusIndex];
            disagreement = (disagreement !== '?' && bp !== disagreement) ? disagreement : "";
            if (this.threshold === thresholdLevels.overFontWidth) {
                this.drawBasePair(this.mainBox, tmpTranslationX, bp, disagreement);
            } else {
                this.drawBasePairPlaceholder(this.mainBox, tmpTranslationX - (this.fontBoxWidth / 2), bp, disagreement);
            }

            consensusIndex++
        })
    }

    drawBasePair(container, translationX, basePair, consensus) {
        const disagreementOffsetY = this.positionsY.disagreementField;
        const sequenceOffsetY = this.positionsY.referenceField;

        container
            .append("text")
            .attr("class", "basePairDisagreement")
            .attr("x", translationX)
            .attr("dy", disagreementOffsetY)
            .style("text-anchor", "middle")
            .style('fill', colors[consensus] || "#c0c0c0")
            .text(consensus);

        container
            .append("text")
            .attr("class", "basePairSequence")
            .attr("x", translationX)
            .attr("dy", sequenceOffsetY)
            .style("text-anchor", "middle")
            .style('fill', colors[basePair])
            .text(basePair);
    }

    updateBasePairNodes() {
        const sequence = this.getSequence(this.edges.left, this.edges.right)

        const basePairNodes = this.mainBox.selectAll("text.basePairSequence").nodes();
        const disagreementNodes = this.mainBox.selectAll("text.basePairDisagreement").nodes();

        let translationX = this.fontBoxWidth / 2;
        let consensusIndex = this.edges.left;

        sequence.forEach((bp, index) => {
            let disagreement = this.consensus[consensusIndex];
            disagreement = disagreement !== "?" && bp !== disagreement ? disagreement : "";
            const tmpTranslationX = translationX;
            if (basePairNodes[index]) {
                disagreementNodes[index].setAttribute("x", tmpTranslationX)
                disagreementNodes[index].style.fill = colors[disagreement] || "#c0c0c0";
                disagreementNodes[index].textContent = disagreement;

                basePairNodes[index].setAttribute("x", tmpTranslationX)
                basePairNodes[index].style.fill = bp === "-" ? "#000" : colors[bp];
                basePairNodes[index].textContent = bp;
            } else {
                this.drawBasePair(this.mainBox, tmpTranslationX, bp, disagreement);
            }
            consensusIndex++
            translationX += this.fontBoxWidth;
        })

        if (basePairNodes.length >= sequence.length) {
            for (let i = sequence.length; i < basePairNodes.length; i++) {
                basePairNodes[i].remove();
                disagreementNodes[i].remove();
            }
        }
    }

    drawBasePairPlaceholder(container, translationX, basePair, disagreementBasePair) {
        const placeholderHeight = 12;
        const opacity = .6;
        const referenceOffsetY = this.positionsY.referenceField - placeholderHeight;
        const disagreementOffsetY = referenceOffsetY - 15;

        container
            .append("rect")
            .attr("class", "basePairDisagreement")
            .attr("x", translationX)
            .attr("y", disagreementOffsetY)
            .attr("width", this.fontBoxWidth)
            .attr("height", placeholderHeight)
            .style('fill', disagreementBasePair ? colors[disagreementBasePair] : "#fff")
            .style('opacity', disagreementBasePair ? opacity : 0);

        container
            .append("rect")
            .attr("class", "basePairSequence")
            .attr("x", translationX)
            .attr("y", referenceOffsetY)
            .attr("width", this.fontBoxWidth)
            .attr("height", placeholderHeight)
            .style('fill', colors[basePair] || "#c0c0c0")
            .style('opacity', opacity);
    }

    updateBasePairPlaceholderNodes() {
        if (this.zoom === zoomLevels.max) {
            this.mainBox.selectAll(".basePairSequence,.basePairDisagreement,.ruler,.tickGroup").remove();
            this.renderRuler()
        } else {
            const reference = this.getSequence(this.edges.left, this.edges.right);

            const basePairNodes = this.mainBox.selectAll("rect.basePairSequence").nodes();
            const disagreementNodes = this.mainBox.selectAll("rect.basePairDisagreement").nodes();

            let translationX = 0;
            let disagreementIndex = this.edges.left;

            reference.forEach((bp, index) => {
                let disagreement = this.consensus[disagreementIndex];

                disagreement = disagreement !== "?" && bp !== disagreement ? disagreement : "";
                const tmpTranslationX = translationX;

                if (basePairNodes[index]) {
                    disagreementNodes[index].setAttribute("x", tmpTranslationX)
                    disagreementNodes[index].setAttribute("width", this.fontBoxWidth - 0.01)
                    if (disagreement !== "") {
                        disagreementNodes[index].style.fill = colors[disagreement] || "#c0c0c0";
                        disagreementNodes[index].style.opacity = .6;
                    } else {
                        disagreementNodes[index].style.fill = "#fff";
                        disagreementNodes[index].style.opacity = 0;
                    }

                    basePairNodes[index].setAttribute("x", tmpTranslationX)
                    basePairNodes[index].setAttribute("width", this.fontBoxWidth)
                    basePairNodes[index].style.fill = colors[bp] || "#c0c0c0";
                } else {
                    this.drawBasePairPlaceholder(this.mainBox, tmpTranslationX
                        , bp, disagreement);
                }
                disagreementIndex++

                translationX += this.fontBoxWidth;
            });

            if (basePairNodes.length >= reference.length) {
                for (let i = reference.length; i < basePairNodes.length; i++) {
                    basePairNodes[i].remove();
                    disagreementNodes[i].remove();
                }
            }
        }
    }

    drawResultMatchSearches() {
        const height = 17
        this.mainBox.selectAll(".matchSearch").remove();

        this.searchResult.data.forEach(r => {
            if (isInRange(r.pos, r.pos + r.textLength, this.edges.left, this.edges.right)) {
                this.mainBox
                    .append("rect")
                    .attr("class", "matchSearch")
                    .attr("y", this.positionsY.resultMatch)
                    .attr("x", ((r.pos - this.offsetScroll.current) * this.fontBoxWidth))
                    .attr("width", (this.searchResult.textLength * this.fontBoxWidth))
                    .attr("height", height)
                    .style("fill", "yellow")
                    .style("opacity", .3)
                    .style("stroke", "#000")
                    .style("stroke-width", 1)
            }
        })
    }

    /**
     *
     * @param data = statusScroll
     */
    slide(data) {
        this.annotationsContainer
            .attr("transform", "translate(" + -(this.offsetScroll.current * this.fontBoxWidth) + ",0)")

        this.renderCoverages()
        this.renderIdentities();

        if (this.referenceLength !== this.sequenceLength) {
            this.drawBorders([this.tempStartOffsetAlignment, this.tempStartOffsetAlignment + this.referenceLength]);
        }

        if (data === "end") {
            this.drawAnnotations();
            this.drawResultMatchSearches();
            this.enableTooltip();
        }

        if (this.threshold >= thresholdLevels.overFontWidth) {
            this.updateBasePairNodes();
        } else {
            this.updateBasePairPlaceholderNodes();
        }
    }

    setFontWidth(data) {
        this.zoom = data;

        this.setEditorParameters();
        this.getEdges(this.maxBasePairsInAreaView, this.sequenceLength);

        this.EventsTrigger("setAreaView", {
            width: this.maxBasePairsInAreaView,
            position: this.offsetScroll.current
        })

        this.mainBox.selectAll(".basePairSequence,.basePairDisagreement,.ruler,.tickGroup").remove();

        this.slide("end")
    }

    getSequence(start, end) {
        return this.sequence.slice(start, end).split("");
    }

    setScroll(scroll) {
        const min = 0
        const max = this.sequenceLength - this.maxBasePairsInAreaView;
        this.offsetScroll.current -= scroll;
        this.offsetScroll.step -= scroll;

        if (this.offsetScroll.current < min || this.offsetScroll.current > max) {
            if (this.offsetScroll.current < min) {
                this.offsetScroll.current = min
            } else {
                this.offsetScroll.current = max
            }
        } else {
            this.edges.left -= scroll;
            this.edges.right -= scroll;
        }
    }

    setComponentPositions(maxBelowLevels) {
        const identityFieldY = this.identities?.length ? 35 : 0;

        this.positionsY = {
            coverageField: 26,
            identityField: 55,
            disagreementField: identityFieldY + 40,
            resultMatch: identityFieldY + 42,
            referenceField: identityFieldY + 55,
            annotationsField: identityFieldY + 65
        }

        if (this.reads.length === 0) {
            this.$readsBoxContainer.style.height = "0px";
            this.$readsBoxContainer.style.minHeight = "0px";

            const $referenceContainerSize = this.$referenceContainer.getBoundingClientRect();
            const newReferenceHeight = (window.innerHeight - $referenceContainerSize.top - bottomPadding);

            this.$referenceContainer.style.height = newReferenceHeight + "px";
            this.$alignmentLabelContainer.style.height = newReferenceHeight + "px";
            this.mainBox.style("height", newReferenceHeight + "px");

            this.brush.setUp(
                this.positionsY.annotationsField +
                this.maxBelowLevels * (this.annotationHeight + this.stepLevelAnnotation)
            );
        }
    }

    setOffsetScroll(data) {
        this.offsetScroll.current = data.current;

        const newBrushSize = {
            x: (this.rangeBp.start - this.offsetScroll.current) * this.fontBoxWidth,
            width: (this.rangeBp.end - this.rangeBp.start) * this.fontBoxWidth,
        }
        this.brush.setParameters(newBrushSize)

        this.middleArea = this.offsetScroll.current + Math.round(this.maxBasePairsInAreaView / 2);
        this.getEdges(this.maxBasePairsInAreaView, this.sequenceLength);

        this.slide("end")

        this.scrollToFirstRead();
    }

    scrollToFirstRead() {
        if (this.reads.length && this.readSlots?.length) {
            this.readSlots.some((slot, index) => {
                slot.some(r => {
                    if (isInRange(r.start, r.end, this.edges.left, this.edges.right)) {
                        this.$readsBoxContainer.scrollTop = index * 100
                        return false;
                    }
                })
            })
        }
    }

    toggleItems(data) {
        this.annotations.forEach(el => {
            if (data.objects.includes(el.pk)) {
                if (!data.visible) {
                    this.hiddenAnnotations.push(el.pk)
                } else {
                    this.hiddenAnnotations = this.hiddenAnnotations.filter(item => item !== el.pk)
                }
                el.visible = data.visible;

                const annotationNodes = this.annotationsContainer.selectAll(".annotation_" + el.pk).nodes();

                annotationNodes.forEach(a => {
                    a.setAttribute("opacity", data.visible ? 1 : 0);
                    if (data.visible) {
                        a.removeAttribute("data-popper-reference-hidden")
                    } else {
                        a.setAttribute("data-popper-reference-hidden", "")
                    }
                })
            }
        });
    }

    EventsTrigger(action, data) {
        if (this.mediator) {
            this.mediator.trigger(action, data, this)
        }
    }

    EventsOn() {
        if (this.mediator) {
            this.mediator.on("setFontWidth", "setFontWidth", this);
            this.mediator.on("hideBrush", "hideBrush", this);
            this.mediator.on("showBrush", "showBrush", this);

            this.mediator.on("setScroll", "setScroll", this);
            this.mediator.on("slide", "slide", this);

            this.mediator.on("setOffsetScroll", "setOffsetScroll", this);

            this.mediator.on("updateBrush", "updateBrush", this);

            this.mediator.on("search", "search", this);
            this.mediator.on("resetSearch", "resetSearch", this);

            this.mediator.on("moveOnSearchResult", "moveOnSearchResult", this);

            this.mediator.on("selectRange", "selectRange", this);
            this.mediator.on("copyBasePairsSelected", "copyBasePairsSelected", this);

            this.mediator.on("toggleObjects", "toggleItems", this)
        }
    }
}